import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { track } from '@vercel/analytics'
import SupabaseLogin from './SupabaseLogin'
import PublicHeader from './PublicHeader'
import AppStoreBadges from './AppStoreBadges'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import StarBorder from './ui/StarBorder'
import { setSEOMetaTags } from '../lib/seoUtils'

const LandingPage = () => {
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showSessionMessage, setShowSessionMessage] = useState(false)
  const [openFAQ, setOpenFAQ] = useState(null)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Links from signup and email verification land here with ?auth=login (#428):
  // there is no /login route, sign-in is this modal.
  const authEmail = searchParams.get('email') || ''
  const authNotice = (() => {
    if (searchParams.get('auth') !== 'login') return ''
    if (searchParams.get('verify_error')) return searchParams.get('verify_error')
    if (searchParams.get('verified') === 'true') return 'Your email is verified. Sign in to get started.'
    if (searchParams.get('check_email') === 'true') {
      return `We sent a verification link to ${authEmail || 'your email'}. Click it, then sign in here.`
    }
    return ''
  })()

  useEffect(() => {
    if (searchParams.get('auth') === 'login') setShowAuthModal(true)
  }, [searchParams])

  // Check for session expiration message
  useEffect(() => {
    if (searchParams.get('message') === 'session_expired') {
      setShowSessionMessage(true)
      const timer = setTimeout(() => {
        setShowSessionMessage(false)
      }, 10000)
      return () => clearTimeout(timer)
    }
  }, [searchParams])

  // SEO: Update document title, meta description, canonical tag, Open Graph, and Twitter Cards
  useEffect(() => {
    setSEOMetaTags({
      title: 'Prosperia CRM | CRM for Pressure Washing & Exterior Cleaning Contractors',
      description: 'The CRM built for pressure washing and exterior cleaning contractors. Measure jobs, send estimates, schedule crews, and get paid — from your browser or the iOS and Android app. Free forever plan, no credit card required.',
      url: 'https://prosperiacrm.com/',
      keywords: 'pressure washing CRM, exterior cleaning software, contractor CRM, roof cleaning CRM, soft washing software, estimate software, contractor scheduling, field service management, free contractor CRM'
    })
  }, [])

  // Track ViewContent event for Meta Pixel
  useEffect(() => {
    try {
      import('../lib/metaPixel').then(({ trackMetaPixelPageNavigation }) => {
        trackMetaPixelPageNavigation('/', 'Landing Page')
      })
    } catch (err) {
      // Meta Pixel import failed, continue without tracking
    }
  }, [])

  const handleOpenAuth = () => {
    setShowAuthModal(true)
  }

  const handleStartTrial = () => {
    track('Start Free - Landing Page')
    try {
      import('../lib/analytics').then(({ analytics }) => {
        analytics.trackTrialStart('landing_page')
      })
    } catch (err) {
      // Analytics import failed, continue without tracking
    }

    try {
      import('../lib/metaPixel').then(({ trackMetaPixelTrialStart }) => {
        trackMetaPixelTrialStart({ source: 'landing_page' })
      })
    } catch (err) {
      // Meta Pixel import failed, continue without tracking
    }

    navigate('/onboarding?entry=trial')
  }

  const handleCloseAuth = () => {
    setShowAuthModal(false)
  }

  const handleDismissMessage = () => {
    setShowSessionMessage(false)
  }

  const handleToggleFAQ = (index) => {
    setOpenFAQ(prev => prev === index ? null : index)
  }

  const faqs = [
    {
      question: 'Is Prosperia really free?',
      answer: 'Yes. The free plan is free forever — not a trial. You get one user, up to 20 clients, estimates, invoices, scheduling, and the Google Maps measurement tool. No credit card required, no time limit. Paid plans start at $39.99/month when you need team features or higher limits.'
    },
    {
      question: 'How is this different from Jobber or Housecall Pro?',
      answer: 'Prosperia is built specifically for exterior cleaning contractors — pressure washing, roof cleaning, soft washing, window washing. The built-in Google Maps measurement tool lets you measure roofs and surfaces without a site visit.'
    },
    {
      question: 'Do I need to install anything?',
      answer: 'No installation is required to get started — Prosperia runs in any modern browser on your laptop, tablet, or phone. For the best on-the-job experience, we also have native apps for iPhone and Android available on the App Store and Google Play.'
    },
    {
      question: 'Can I import my existing customers?',
      answer: 'Yes. You can import your client list via CSV. If you need help migrating from another platform, reach out to our support team and we will walk you through it.'
    },
    {
      question: 'How do payments work?',
      answer: 'Prosperia uses Stripe Connect so you can accept credit and debit cards directly from your customers. Funds go straight to your bank account. Standard Stripe card processing fees apply.'
    },
    {
      question: 'What if I need help getting set up?',
      answer: 'Setup takes about 5 minutes. If you get stuck, our support team is available to help you get your account configured, your services set up, and your first estimate sent. Visit the support page or email us directly.'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <header>
        <PublicHeader onOpenAuth={handleOpenAuth} onStartTrial={handleStartTrial} />
      </header>

      <main>

      {/* Session Expiration Message */}
      {showSessionMessage && (
        <div className="bg-gradient-to-r from-amber-900/20 to-orange-900/20 border-l-4 border-amber-500 p-4 shadow-sm animate-slide-down backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <i className="fas fa-exclamation-triangle text-amber-400 text-lg"></i>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-slate-700 font-medium">
                    <strong>Session Expired:</strong> Your session has expired. Please sign in again to continue.
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={handleOpenAuth}
                  size="sm"
                  className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
                >
                  Sign In
                </Button>
                <button
                  onClick={handleDismissMessage}
                  className="text-slate-600 hover:text-slate-800 transition-colors duration-300 hover:scale-110 p-1"
                  aria-label="Dismiss message"
                >
                  <i className="fas fa-times text-lg"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* HERO SECTION */}
      {/* ============================================ */}
      <section aria-labelledby="hero-heading" className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 min-h-[calc(92vh-5rem)] flex items-center pt-4 sm:pt-5 md:pt-6 lg:pt-7 pb-6 sm:pb-8 md:pb-10 lg:pb-12 overflow-hidden">
        <div className="w-full px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-5xl mx-auto flex flex-col items-center text-center">
            {/* Headline */}
            <h1
              id="hero-heading"
              className="text-[2rem] sm:text-[2.25rem] md:text-5xl lg:text-[3.5rem] xl:text-6xl font-bold text-white leading-[1.1] max-w-4xl"
            >
              More time, less headache.
            </h1>

            {/* Subheadline */}
            <p className="mt-5 sm:mt-6 text-base sm:text-lg md:text-xl text-slate-300 max-w-[52ch] leading-relaxed">
              The CRM built for Exterior Cleaners. Schedule, manage, get paid.
            </p>

            {/* CTA */}
            <Button
              onClick={handleStartTrial}
              size="lg"
              className="mt-7 sm:mt-8 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-10 sm:px-14 py-5 sm:py-7 text-lg sm:text-xl md:text-2xl font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105"
              aria-label="Start Free"
              tabIndex={0}
            >
              Start Free
            </Button>
            <p className="text-sm sm:text-base text-slate-400 mt-4">
              Free forever plan &bull; No credit card required &bull; 5-minute setup
            </p>

            {/* App store badges */}
            <div className="mt-5 sm:mt-6">
              <AppStoreBadges source="landing_hero" size="xs" align="center" />
            </div>

            {/* Hero image (centered below, sized to stay above the fold) */}
            <div className="relative mt-8 sm:mt-10 w-full max-w-3xl lg:max-w-4xl">
              {/* Teal glow behind/under image */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -inset-x-10 -bottom-16 h-2/3 rounded-[50%] bg-teal-400/30 blur-3xl"
              />
              <picture className="relative block drop-shadow-2xl">
                <source srcSet="/icons/landingpage_photo.webp" type="image/webp" />
                <img
                  src="/icons/landingpage_photo.jpg"
                  alt="Prosperia CRM dashboard and mobile measurement app"
                  width="1920"
                  height="1080"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  className="w-full h-auto"
                  style={{
                    WebkitMaskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
                    maskImage: 'linear-gradient(to bottom, black 70%, transparent 100%)',
                  }}
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* JOBS TO BE DONE — 3 CARDS */}
      {/* ============================================ */}
      <section aria-labelledby="jtbd-heading" className="relative py-16 sm:py-20 lg:py-28 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 xl:px-16 relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <h2 id="jtbd-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              One place for every side of the business.
            </h2>
          </div>

          <div className="flex flex-col gap-16 sm:gap-20 lg:gap-24">
            {/* Quote to Payment — image right */}
            <Link
              to="/features?category=quote-to-payment"
              onClick={() => track('Landing Pillar - Quote to Payment')}
              aria-label="Explore Quote to Payment features"
              className="group grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-4 focus-visible:ring-offset-slate-900"
            >
              <div>
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-5 border border-teal-500/30 group-hover:scale-110 transition-transform duration-300">
                  <i className="fas fa-file-invoice-dollar text-teal-400 text-2xl"></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 group-hover:text-teal-300 transition-colors duration-300">
                  Quote to Payment
                </h3>
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                  Build branded estimates in minutes, turn approvals into scheduled jobs, and invoice in a click. Customers pay online in seconds, while optional line items, deposits, and expense tracking keep every job profitable from quote to close-out.
                </p>
              </div>
              <div className="flex items-center justify-center">
                <picture>
                  <source srcSet="/icons/Estimate%2Bphone.webp" type="image/webp" />
                  <img
                    src="/icons/Estimate%2Bphone.png"
                    alt="Estimate displayed on a phone"
                    loading="lazy"
                    className="w-full max-w-md h-auto object-contain drop-shadow-2xl group-hover:scale-[1.02] transition-transform duration-300"
                  />
                </picture>
              </div>
            </Link>

            {/* Scheduling & Team — image left */}
            <Link
              to="/features?category=scheduling-team"
              onClick={() => track('Landing Pillar - Scheduling & Team')}
              aria-label="Explore Scheduling & Team features"
              className="group grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-4 focus-visible:ring-offset-slate-900"
            >
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl aspect-[16/9] order-2 lg:order-1 group-hover:scale-[1.02] transition-transform duration-300">
                <picture>
                  <source srcSet="/icons/Scheduling.webp" type="image/webp" />
                  <img
                    src="/icons/Scheduling.jpg"
                    alt="Shared scheduling calendar for jobs and crews"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </picture>
              </div>
              <div className="order-1 lg:order-2">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-5 border border-teal-500/30 group-hover:scale-110 transition-transform duration-300">
                  <i className="fas fa-calendar-alt text-teal-400 text-2xl"></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 group-hover:text-teal-300 transition-colors duration-300">
                  Scheduling & Team
                </h3>
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                  Manage jobs and crews on a shared calendar your team sees in real time. Control employee access and permissions, attach photos and documents to every job, and ping the crew with quick status updates so everyone knows what&apos;s next.
                </p>
              </div>
            </Link>

            {/* Tools for the Trade — image right */}
            <Link
              to="/features?category=tools-for-the-trade"
              onClick={() => track('Landing Pillar - Tools for the Trade')}
              aria-label="Explore Tools for the Trade features"
              className="group grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-4 focus-visible:ring-offset-slate-900"
            >
              <div>
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-5 border border-teal-500/30 group-hover:scale-110 transition-transform duration-300">
                  <i className="fas fa-ruler-combined text-teal-400 text-2xl"></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 group-hover:text-teal-300 transition-colors duration-300">
                  Tools for the Trade
                </h3>
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                  Measure roofs and surfaces on Google Maps without leaving your desk, capture site dimensions on the spot with AR right from your phone, and dial in mix ratios every time with the built-in chemical calculator.
                </p>
              </div>
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl aspect-[16/9] group-hover:scale-[1.02] transition-transform duration-300">
                <picture>
                  <source srcSet="/icons/Tools.webp" type="image/webp" />
                  <img
                    src="/icons/Tools.jpg"
                    alt="Trade tools — roof measurement, AR, and chemical calculator"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </picture>
              </div>
            </Link>

            {/* Growth & Automation — image left */}
            <Link
              to="/features?category=growth-automation"
              onClick={() => track('Landing Pillar - Growth & Automation')}
              aria-label="Explore Growth & Automation features"
              className="group grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-4 focus-visible:ring-offset-slate-900"
            >
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl aspect-[16/9] order-2 lg:order-1 group-hover:scale-[1.02] transition-transform duration-300">
                <picture>
                  <source srcSet="/icons/Automation.webp" type="image/webp" />
                  <img
                    src="/icons/Automation.png"
                    alt="Growth and automation tools — campaigns, AI assistant, and follow-ups"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </picture>
              </div>
              <div className="order-1 lg:order-2">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-5 border border-teal-500/30 group-hover:scale-110 transition-transform duration-300">
                  <i className="fas fa-bolt text-teal-400 text-2xl"></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 group-hover:text-teal-300 transition-colors duration-300">
                  Growth & Automation
                </h3>
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                  Stay in front of customers with SMS and email campaigns, generate AI estimates with price assist that suggests competitive line items, let the AI assistant draft replies and recap the day, automate the follow-ups and reminders that win repeat work, and drop a one-tap review link into every completed job.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* MOBILE APP — iOS & ANDROID */}
      {/* ============================================ */}
      <section aria-labelledby="mobile-app-heading" className="relative py-16 sm:py-20 lg:py-28 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/30 text-teal-300 text-xs font-semibold uppercase tracking-wider mb-5">
            <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></span>
            Now Available
          </div>
          <h2 id="mobile-app-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Take Prosperia anywhere.<br />
            <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Now on iOS and Android.</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-8 max-w-xl mx-auto">
            Run your day from the truck. Send estimates from the driveway, measure roofs with AR, capture job photos, and collect payments — all from the native app built for field crews.
          </p>

          <ul className="space-y-3 mb-8 max-w-md mx-auto text-left">
            <li className="flex items-start text-slate-300">
              <i className="fas fa-check-circle text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
              <span>Build estimates and invoices on the spot</span>
            </li>
            <li className="flex items-start text-slate-300">
              <i className="fas fa-check-circle text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
              <span>AR ruler and on-site measurement tools</span>
            </li>
            <li className="flex items-start text-slate-300">
              <i className="fas fa-check-circle text-teal-400 mr-3 mt-1 flex-shrink-0"></i>
              <span>Push notifications for new jobs and payments</span>
            </li>
          </ul>

          <AppStoreBadges source="landing_page" size="lg" align="center" />
          <p className="text-sm text-slate-400 mt-5">
            Free to download &bull; Works with your existing Prosperia account
          </p>
        </div>
      </section>

      {/* ============================================ */}
      {/* PRICING TEASER */}
      {/* ============================================ */}
      <section aria-labelledby="pricing-heading" className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center relative z-10">
          <h2 id="pricing-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Free to start. Fair when you grow.
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl mx-auto">
            Free forever for solo contractors. Paid plans for teams start at $39.99/month.
          </p>
          <Link
            to="/pricing"
            onClick={() => track('View Pricing - Landing Page')}
            className="inline-flex items-center text-teal-400 hover:text-teal-300 text-lg font-semibold transition-colors duration-300 group"
          >
            <span className="mr-2">See full pricing details</span>
            <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform duration-300"></i>
          </Link>
        </div>
      </section>

      {/* ============================================ */}
      {/* FAQ SECTION */}
      {/* ============================================ */}
      <section aria-labelledby="faq-heading" className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-900 overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="text-center mb-10 sm:mb-14">
            <h2 id="faq-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
              Frequently asked questions
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-slate-700/50 rounded-xl overflow-hidden bg-elevated/30 backdrop-blur-sm">
                <button
                  onClick={() => handleToggleFAQ(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleToggleFAQ(index)
                    }
                  }}
                  className="w-full flex items-center justify-between px-5 sm:px-6 py-4 sm:py-5 text-left focus:outline-none focus:ring-2 focus:ring-teal-500/50 rounded-xl transition-colors duration-200 hover:bg-overlay-subtle"
                  aria-expanded={openFAQ === index}
                  aria-controls={`faq-answer-${index}`}
                  tabIndex={0}
                >
                  <span className="text-base sm:text-lg font-semibold text-white pr-4">{faq.question}</span>
                  <i className={`fas fa-chevron-down text-teal-400 text-sm transition-transform duration-300 flex-shrink-0 ${openFAQ === index ? 'rotate-180' : ''}`}></i>
                </button>
                <div
                  id={`faq-answer-${index}`}
                  role="region"
                  aria-labelledby={`faq-question-${index}`}
                  className={`overflow-hidden transition-all duration-300 ${openFAQ === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <p className="px-5 sm:px-6 pb-5 text-slate-300 leading-relaxed text-sm sm:text-base">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FINAL CTA SECTION */}
      {/* ============================================ */}
      <section aria-labelledby="cta-heading" className="bg-gradient-to-br from-slate-900 via-teal-900 to-cyan-900 py-16 sm:py-20 md:py-28 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-64 h-64 bg-teal-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center relative z-10">
          <h2 id="cta-heading" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 sm:mb-8">
            Start growing your business today.
          </h2>
          <div className="flex flex-col items-center">
            <div className="hover:scale-105 active:scale-95 transition-transform duration-200">
              <StarBorder
                as="div"
                color="teal"
                speed="6s"
                thickness={2}
                className="rounded-lg"
              >
                <Button
                  size="lg"
                  onClick={handleStartTrial}
                  className="w-full sm:w-auto bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-10 sm:px-14 py-5 sm:py-7 text-lg sm:text-xl md:text-2xl font-bold shadow-2xl hover:shadow-3xl transition-all duration-300"
                  aria-label="Start Free"
                  tabIndex={0}
                >
                  Start Free
                </Button>
              </StarBorder>
            </div>
            <p className="text-sm sm:text-base text-slate-400 mt-5">
              Free forever plan. No credit card. 5 minutes to your first estimate.
            </p>
          </div>
        </div>
      </section>

      </main>

      {/* Modern Footer */}
      <footer className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-6">
                <picture>
                  <source srcSet="/icons/logo_1024x1024-noBGm.webp" type="image/webp" />
                  <img
                    src="/icons/logo_1024x1024-noBGm.png"
                    alt="Prosperia Logo"
                    className="h-11 w-11 object-contain -ml-1"
                    width="44"
                    height="44"
                    loading="lazy"
                  />
                </picture>
                <span className="text-2xl font-bold text-white">Prosperia CRM</span>
              </div>
              <p className="text-slate-400 leading-relaxed mb-6">
                The complete CRM for exterior cleaning contractors. Measure, estimate, schedule, and get paid — from any browser or the iOS and Android app.
              </p>
              <div className="flex space-x-4">
                <a href="https://www.linkedin.com/company/prosperiacrm" target="_blank" rel="noopener noreferrer" onClick={() => track('Footer - LinkedIn')} className="text-slate-400 hover:text-teal-400 transition-colors duration-300 hover:scale-125">
                  <i className="fab fa-linkedin text-xl"></i>
                </a>
                <a href="https://www.facebook.com/people/Prosperia-CRM/61583908240191/" target="_blank" rel="noopener noreferrer" onClick={() => track('Footer - Facebook')} className="text-slate-400 hover:text-teal-400 transition-colors duration-300 hover:scale-125">
                  <i className="fab fa-facebook text-xl"></i>
                </a>
                <a href="https://www.youtube.com/@ProsperiaCRM" target="_blank" rel="noopener noreferrer" onClick={() => track('Footer - YouTube')} className="text-slate-400 hover:text-teal-400 transition-colors duration-300 hover:scale-125">
                  <i className="fab fa-youtube text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-6">Product</h3>
              <ul className="space-y-3 text-slate-400">
                <li><Link to="/pricing" onClick={() => track('Footer - Pricing')} className="hover:text-teal-400 transition-colors duration-300">Pricing</Link></li>
                <li><Link to="/features" onClick={() => track('Footer - Features')} className="hover:text-teal-400 transition-colors duration-300">Features</Link></li>
                <li><Link to="/prosperia-ai" onClick={() => track('Footer - Prosperia AI')} className="hover:text-teal-400 transition-colors duration-300">Prosperia AI</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-6">Company</h3>
              <ul className="space-y-3 text-slate-400">
                <li><Link to="/about" onClick={() => track('Footer - About')} className="hover:text-teal-400 transition-colors duration-300">About</Link></li>
                <li><Link to="/support" onClick={() => track('Footer - Support')} className="hover:text-teal-400 transition-colors duration-300">Support</Link></li>
                <li><Link to="/blog" onClick={() => track('Footer - Blog')} className="hover:text-teal-400 transition-colors duration-300">Blog</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-6">Resources</h3>
              <ul className="space-y-3 text-slate-400">
                <li><Link to="/faqs" onClick={() => track('Footer - Help Center')} className="hover:text-teal-400 transition-colors duration-300">Help Center</Link></li>
                <li><Link to="/faqs" onClick={() => track('Footer - Documentation')} className="hover:text-teal-400 transition-colors duration-300">Documentation</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-slate-400 text-sm">
                &copy; {new Date().getFullYear()} Prosperia CRM. All rights reserved.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <Link to="/privacy" onClick={() => track('Footer - Privacy Policy')} className="text-slate-400 hover:text-teal-400 text-sm transition-colors duration-300">
                  Privacy Policy
                </Link>
                <Link to="/terms" onClick={() => track('Footer - Terms of Service')} className="text-slate-400 hover:text-teal-400 text-sm transition-colors duration-300">
                  Terms of Service
                </Link>
                <Link to="/cookies" onClick={() => track('Footer - Cookie Policy')} className="text-slate-400 hover:text-teal-400 text-sm transition-colors duration-300">
                  Cookie Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fade-in">
          <Card className="max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl border-2 border-slate-700 animate-slide-up bg-slate-800">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-6 bg-gradient-to-r from-teal-900/30 to-cyan-900/30 border-b border-slate-700">
              <CardTitle className="text-2xl font-bold text-white">
                Sign Into Your Account
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseAuth}
                className="h-8 w-8 p-0 hover:bg-slate-700 hover:scale-110 transition-all duration-300 text-slate-300"
                aria-label="Close modal"
              >
                <i className="fas fa-times"></i>
              </Button>
            </CardHeader>
            <CardContent className="p-8">
              <SupabaseLogin
                onSuccess={handleCloseAuth}
                redirectTo="/dashboard"
                initialEmail={authEmail}
                notice={authNotice}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

export default LandingPage
